import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Wifi, WifiOff, RefreshCw, AlertCircle } from 'lucide-react';
import { api } from '@/services/api';

interface OfflineStatus {
  isSyncing: boolean;
  pendingTransactions: number;
  lastSync?: Date;
  transactions: Array<{
    id: string;
    type: string;
    attempts: number;
    lastAttemptAt: string;
    createdAt: string;
  }>;
}

const OfflineStatusIndicator: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: status, isLoading } = useQuery<OfflineStatus>({
    queryKey: ['offline', 'status'],
    queryFn: async () => {
      const res = await api.get('/api/offline/status');
      return res.data;
    },
    refetchInterval: 30000, // Refetch every 30 seconds
  });

  const syncMutation = useMutation({
    mutationFn: async () => {
      const res = await api.post('/offline/sync');
      return res.data;
    },
    onSuccess: () => {
      // Refetch status after sync
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
  });

  const handleManualSync = () => {
    syncMutation.mutate();
  };

  if (isLoading) {
    return (
      <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 border border-gray-200">
        <div className="flex items-center space-x-2">
          <RefreshCw className="h-4 w-4 text-gray-400 animate-spin" />
          <span className="text-sm text-gray-600">Loading...</span>
        </div>
      </div>
    );
  }

  const hasPendingTransactions = status?.pendingTransactions && status.pendingTransactions > 0;
  const isOffline = status?.isSyncing === false && hasPendingTransactions;

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className={`relative ${isExpanded ? 'w-80' : ''}`}>
        {/* Status Indicator */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`bg-white rounded-lg shadow-lg border p-3 flex items-center space-x-2 transition-all duration-200 ${
            isOffline 
              ? 'border-red-200 bg-red-50' 
              : hasPendingTransactions 
                ? 'border-yellow-200 bg-yellow-50'
                : 'border-green-200 bg-green-50'
          }`}
        >
          {isOffline ? (
            <WifiOff className="h-4 w-4 text-red-500" />
          ) : hasPendingTransactions ? (
            <AlertCircle className="h-4 w-4 text-yellow-500" />
          ) : (
            <Wifi className="h-4 w-4 text-green-500" />
          )}
          
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium">
              {isOffline ? 'Offline' : hasPendingTransactions ? 'Pending Sync' : 'Online'}
            </span>
            {hasPendingTransactions && (
              <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-full">
                {status.pendingTransactions}
              </span>
            )}
          </div>

          {syncMutation.isPending && (
            <RefreshCw className="h-4 w-4 text-blue-500 animate-spin" />
          )}
        </button>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="absolute bottom-full right-0 mb-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-80">
            <div className="space-y-3">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-gray-900">Offline Status</h3>
                <button
                  onClick={handleManualSync}
                  disabled={syncMutation.isPending}
                  className="flex items-center space-x-1 text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600 disabled:opacity-50"
                >
                  <RefreshCw className={`h-3 w-3 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
                  Sync Now
                </button>
              </div>

              {/* Status Info */}
              <div className="text-xs text-gray-600 space-y-1">
                <div className="flex justify-between">
                  <span>Status:</span>
                  <span className={`font-medium ${
                    isOffline ? 'text-red-600' : hasPendingTransactions ? 'text-yellow-600' : 'text-green-600'
                  }`}>
                    {isOffline ? 'Offline' : hasPendingTransactions ? 'Pending' : 'Online'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Pending:</span>
                  <span className="font-medium">{status?.pendingTransactions || 0}</span>
                </div>
                {status?.lastSync && (
                  <div className="flex justify-between">
                    <span>Last Sync:</span>
                    <span className="font-medium">
                      {new Date(status.lastSync).toLocaleTimeString()}
                    </span>
                  </div>
                )}
              </div>

              {/* Recent Transactions */}
              {status?.transactions && status.transactions.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-900 mb-2">Recent Transactions</h4>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {status.transactions.slice(0, 5).map((transaction) => (
                      <div key={transaction.id} className="text-xs bg-gray-50 p-2 rounded">
                        <div className="flex justify-between items-center">
                          <span className="font-medium capitalize">
                            {transaction.type.replace('_', ' ')}
                          </span>
                          <span className="text-gray-500">
                            {transaction.attempts} attempts
                          </span>
                        </div>
                        <div className="text-gray-500">
                          {new Date(transaction.lastAttemptAt).toLocaleString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Help Text */}
              <div className="text-xs text-gray-500 border-t pt-2">
                <p>• Sales are saved locally when offline</p>
                <p>• Automatic sync resumes when online</p>
                <p>• Failed retries use exponential backoff</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OfflineStatusIndicator;
